import { styled } from "goober";
import { formatTinesUnits } from "../../lib/formatTinesUnits";
import { monthlyTinesUnitsLimit } from "../../lib/session";
import { useMakeUrl, routes } from "../../lib/urls";
import { TinesUnitsBreakdown } from "./TinesUnitsBreakdown";
import { HelpTooltip } from "../HelpTooltip";
import { isInAppNavigation, Link } from "../Link";
import { Text } from "../Text";
import { useMonitoringInteraction } from "./analytics";
import { USAGE_FILL, usageStatus } from "./tinesUnitsStatus";
import { useMonitoringWorkflows } from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

const GaugeContainer = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    flex: none;
    min-height: 32px;
  }
`;

const GaugeLink = styled(Link)`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  text-decoration: none;

  @media (max-width: 900px) {
    flex: none;
    min-height: 32px;
  }
`;

const GaugeBar = styled("div")`
  width: 6px;
  height: 100%;
  border-radius: 999px;
  background: var(--alpha8);
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;
  transition:
    width 120ms ease,
    height 120ms ease;

  &:hover {
    width: 9px;
  }

  @media (max-width: 900px) {
    width: 100%;
    height: 6px;
    flex-direction: row;

    &:hover {
      width: 100%;
      height: 9px;
    }
  }
`;

const GaugeFill = styled("div")`
  flex-basis: 0;
  min-width: 0;
  min-height: 0;
  border-radius: 999px;
`;

const GaugeSegment = styled("div")`
  flex-basis: 0;
  min-width: 0;
  min-height: 0;
`;

const Rail = styled("div")`
  flex: 0 0 120px;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    flex: none;
  }
`;

const RailPanel = styled(Panel)`
  flex: 1;
  padding: 32px 16px;
`;

const TitleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BottomLabel = styled("div")`
  text-align: center;
`;

export function TinesUnitsPanel() {
  const makeUrl = useMakeUrl();
  const { report, loading } = useMonitoringWorkflows();
  const recordInteraction = useMonitoringInteraction();

  const limit = monthlyTinesUnitsLimit();
  if (limit === 0) return null;

  if (loading) {
    return (
      <Rail>
        <PanelSkeleton />
      </Rail>
    );
  }

  const units = report.units;
  if (!units) return null;

  const canDrill = report.unitsBySpace != null;
  const recordOpen = () => {
    recordInteraction("workflows.panel_drilldown_open", { value: "units" });
  };
  const breakdownUrl = makeUrl(routes.monitoring.workflows, {
    panel: "units",
    space: "",
  });

  const fraction = units.total / limit;
  const fill = USAGE_FILL[usageStatus(fraction)];
  const percent = Math.round(fraction * 100);
  const headroom = Math.max(0, limit - units.total);
  const gauge = (
    <GaugeBar>
      <GaugeFill style={{ flexGrow: units.total, background: fill }} />
      {headroom > 0 && <GaugeSegment style={{ flexGrow: headroom }} />}
    </GaugeBar>
  );

  return (
    <Rail>
      <RailPanel>
        <PanelHeader style={{ alignItems: "center" }}>
          <TitleRow>
            <Text variant="text-small-md">Tines units</Text>
            <HelpTooltip content="A single measure of your usage this month, shown against your plan limit." />
          </TitleRow>
          <TotalRow>
            <Text variant="display-md">{percent}%</Text>
          </TotalRow>
        </PanelHeader>

        {canDrill ? (
          <GaugeLink
            to={breakdownUrl}
            aria-label="View Tines units breakdown"
            onPointerDown={(event) => {
              if (isInAppNavigation(event)) recordOpen();
            }}
            onClick={(event) => {
              if (event.detail === 0) recordOpen();
            }}
          >
            {gauge}
          </GaugeLink>
        ) : (
          <GaugeContainer>{gauge}</GaugeContainer>
        )}

        <BottomLabel>
          <Text variant="text-small" color="var(--text-secondary)">
            {formatTinesUnits(units.total)} / {formatTinesUnits(limit)}
          </Text>
        </BottomLabel>
      </RailPanel>
      {canDrill && <TinesUnitsBreakdown />}
    </Rail>
  );
}
